import {
  BatchRecordIngredient,
  CreateBatchRecordIngredientRequest,
  UpdateBatchRecordIngredientRequest
} from '../types/batchRecordIngredient';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';

export const batchRecordIngredientApiService = {
  /**
   * Fetch all batch record ingredients
   */
  fetchAllBatchRecordIngredients: async (): Promise<BatchRecordIngredient[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/batch-record-ingredients`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch batch record ingredients');
      }

      return data.batchRecordIngredients;
    } catch (error) {
      console.error('Error fetching batch record ingredients from API:', error);
      throw error;
    }
  },

  /**
   * Fetch batch record ingredients for a specific batch record
   */
  fetchBatchRecordIngredientsByBatchRecordId: async (batchRecordId: string): Promise<BatchRecordIngredient[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/batch-record-ingredients/by-batch-record/${batchRecordId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch batch record ingredients');
      }

      return data.batchRecordIngredients;
    } catch (error) {
      console.error('Error fetching batch record ingredients by batch record ID:', error);
      throw error;
    }
  },

  /**
   * Create a new batch record ingredient
   */
  createBatchRecordIngredient: async (ingredient: CreateBatchRecordIngredientRequest): Promise<BatchRecordIngredient> => {
    try {
      const response = await fetch(`${API_BASE_URL}/batch-record-ingredients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ingredient)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create batch record ingredient');
      }

      return data.batchRecordIngredient;
    } catch (error) {
      console.error('Error creating batch record ingredient:', error);
      throw error;
    }
  },

  /**
   * Update an existing batch record ingredient
   */
  updateBatchRecordIngredient: async (id: string, ingredient: UpdateBatchRecordIngredientRequest): Promise<BatchRecordIngredient> => {
    try {
      const response = await fetch(`${API_BASE_URL}/batch-record-ingredients/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ingredient)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update batch record ingredient');
      }

      return data.batchRecordIngredient;
    } catch (error) {
      console.error('Error updating batch record ingredient:', error);
      throw error;
    }
  },

  /**
   * Delete a batch record ingredient by ID
   */
  deleteBatchRecordIngredient: async (id: string): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/batch-record-ingredients/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete batch record ingredient');
      }
    } catch (error) {
      console.error('Error deleting batch record ingredient:', error);
      throw error;
    }
  }
};
